import { type ComponentType, useState } from 'react';

import { useIntl } from '../../hooks';
import { cn } from '../../lib/utils';
import { type Device } from '../../types';
import { type DeviceCapability } from '../../types/device-capability.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui';
import DeviceScanRangesPanel from './DeviceScanRangesPanel';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import DeviceWhitelistPanel from './DeviceWhitelistPanel';

interface DeviceSettingsDialogProps {
  device: Device;
  onOpenChange: () => void;
}

// Tailwind only emits classes it finds as literals, so a template-built column class would vanish.
const TAB_GRID_COLUMNS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export default function DeviceSettingsDialog({ device, onOpenChange }: DeviceSettingsDialogProps) {
  const intl = useIntl();
  const tabs: {
    name: string;
    label: string;
    capability?: DeviceCapability;
    Component: ComponentType<{ device: Device }>;
  }[] = [
    {
      name: 'Settings',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.settings' }),
      Component: DeviceSettingsPanel,
    },
    {
      name: 'Whitelist',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.whitelist' }),
      capability: 'frequency_whitelist',
      Component: DeviceWhitelistPanel,
    },
    {
      name: 'Scan ranges',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.scanRanges' }),
      capability: 'scan_ranges',
      Component: DeviceScanRangesPanel,
    },
  ];
  const visibleTabs = tabs.filter(
    ({ capability }) => !capability || device.capabilities.includes(capability),
  );
  const [activeTab, setActiveTab] = useState(tabs[0].name);
  const settingsOnly = visibleTabs.length === 1;
  // Reset rather than derive a fallback: a derived one leaves `activeTab` on the revoked tab, so
  // the dialog would jump back to it, unprompted, if the capability returned.
  if (!visibleTabs.some(({ name }) => name === activeTab)) {
    setActiveTab(visibleTabs[0].name);
  }
  // Without a TabsList the panel has no trigger to be labelled by, so it is not a tabpanel.
  const settingsOnlyContentProps = settingsOnly
    ? { role: undefined, 'aria-labelledby': undefined, tabIndex: undefined }
    : {};

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      {/* overflow-clip (not -hidden): an overflow:hidden box is still programmatically
          scrollable, so focusing the sr-only directionality radio makes the browser
          scroll this container to "reveal" it, detaching the footer. clip can't scroll. */}
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col overflow-clip">
        <DialogTitle hidden></DialogTitle>
        <div className="flex-1 overflow-hidden">
          {/* One Tabs tree for every capability set, so gaining a tab does not remount the settings panel. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            {!settingsOnly && (
              <TabsList
                className={cn(
                  'grid w-full shrink-0 bg-transparent border-b border-border rounded-none p-0',
                  TAB_GRID_COLUMNS[visibleTabs.length],
                )}
              >
                {visibleTabs.map(({ name, label }) => (
                  <TabsTrigger
                    key={`${name}-tab-trigger`}
                    value={name}
                    className="data-[state=active]:bg-accent-light data-[state=active]:text-accent data-[state=active]:border-b-2 data-[state=active]:border-accent-dark rounded-none border-b-2 border-transparent"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            )}

            <div className="flex-1 overflow-hidden">
              {visibleTabs.map(({ name, Component }) => (
                <TabsContent
                  key={`${name}-tab-content`}
                  value={name}
                  className="h-full overflow-hidden"
                  {...settingsOnlyContentProps}
                >
                  <Component device={device} />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
